<template>
  <Modal
    v-if="show"
    :show="true"
    :elem-id="`users-acesso-${atributo}`"
    :title="`${t('Acesso')} ${grupo}`"
    :saveLabel="t('Confirmar')"
    :saveFn="handleConfirm"
    :save-test-id="`users-acesso-${atributo}-confirm`"
    :closeLabel="t('Cancelar')"
    :close-test-id="`users-acesso-${atributo}-cancel`"
    size="large"
    @close="emit('close')"
    @dismiss="emit('close')"
  >
    <div class="acesso-modal" :data-testid="`users-acesso-${atributo}-modal`">
      <p class="acesso-modal-hint">
        {{ t('Marque o que esta pessoa pode acessar. Opções que exigem a mesma permissão são liberadas juntas.') }}
      </p>

      <div class="acesso-modal-toolbar">
        <input-toggle
          :elem-id="`acesso-${atributo}-all`"
          :model-value="allChecked"
          :label="t('Todas as opções')"
          :data-testid="`users-acesso-${atributo}-toggle-all`"
          @update:model-value="(value: boolean) => toggleAll(value)"
        />
        <span class="acesso-modal-count" :data-testid="`users-acesso-${atributo}-count`">
          {{ checked.size }} / {{ rows.length }} {{ t('liberadas') }}
        </span>
      </div>

      <data-table
        :test-id-prefix="`users-acesso-${atributo}`"
        :config="tableConfig"
        :data="rows"
        :auto-load="false"
        display-mode="table"
        @checkbox-change="handleCheckboxChange"
      />
    </div>
  </Modal>
</template>

<script setup lang="ts">
/**
 * AcessoDeGrupoModal — o modal de TODO atributo destravado por grupo (skill `app-groups`): uma
 * linha por opção, "marcar todas", e opções que compartilham o escopo marcadas juntas. Nada é por
 * atributo: título = o grupo, rótulos fixos, opções vindas do serviço dono.
 */
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { DataTableConfig } from '@/grid/types';
import { opcoesConcedidas, escoposDasOpcoes, opcoesComMesmoEscopo, type OpcaoDeAcesso } from './escoposDeAcesso';

const { t } = useI18n();

interface OpcaoRow extends Record<string, unknown> {
  id: string;
  rotulo: string;
  grupo: string;
  escopo: string;
  descricao: string;
  sensivel: string;
  liberada: boolean;
}

const props = defineProps<{
  show: boolean;
  /** O atributo (sem o prefixo custom:) — compõe os data-testid. */
  atributo: string;
  /** O grupo que o destrava — é o título. */
  grupo: string;
  opcoes: OpcaoDeAcesso[];
  /** O valor atual do atributo (escopos separados por espaço). */
  modelValue: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
  close: [];
}>();

const checked = ref<Set<string>>(new Set());

watch(
  () => [props.show, props.modelValue, props.opcoes] as const,
  () => {
    if (props.show) checked.value = opcoesConcedidas(props.modelValue, props.opcoes);
  },
  { immediate: true }
);

const rows = computed<OpcaoRow[]>(() =>
  props.opcoes.map((opcao) => ({
    id: opcao.id,
    rotulo: t(opcao.rotulo),
    grupo: opcao.grupo ?? '',
    escopo: opcao.escopo,
    descricao: opcao.descricao ?? '',
    sensivel: opcao.sensivel ? t('Sensível') : '',
    liberada: checked.value.has(opcao.id)
  }))
);

const allChecked = computed<boolean>(() => rows.value.length > 0 && checked.value.size === rows.value.length);

const tableConfig = computed<DataTableConfig>(() => ({
  tableId: `acesso-${props.atributo}-table`,
  columns: [
    {
      data: 'liberada',
      title: t('Liberada'),
      width: '6rem',
      renderType: 'checkbox',
      checkboxIsChecked: (row: Record<string, unknown>) => checked.value.has(String(row.id))
    },
    { data: 'rotulo', title: t('Opção'), sortable: true },
    ...(props.opcoes.some((o) => o.grupo) ? [{ data: 'grupo', title: t('Categoria'), sortable: true }] : []),
    { data: 'escopo', title: t('Permissão'), sortable: true },
    ...(props.opcoes.some((o) => o.sensivel) ? [{ data: 'sensivel', title: t('Sensível'), width: '7rem' }] : []),
    ...(props.opcoes.some((o) => o.descricao) ? [{ data: 'descricao', title: t('Descrição') }] : [])
  ],
  pagination: false,
  sortable: true,
  size: 'small',
  stripedRows: true,
  heightMode: 'auto'
}));

const setOpcoes = (ids: Iterable<string>, value: boolean): void => {
  const next = new Set(checked.value);
  for (const id of ids) {
    if (value) next.add(id); else next.delete(id);
  }
  checked.value = next;
};

/** Marcar uma opção marca todas que compartilham o escopo dela (são liberadas juntas). */
const handleCheckboxChange = (payload: { rowData: OpcaoRow; checked: boolean }): void => {
  setOpcoes(opcoesComMesmoEscopo(payload.rowData.id, props.opcoes), payload.checked);
};

const toggleAll = (value: boolean): void => {
  setOpcoes(rows.value.map((row) => row.id), value);
};

const handleConfirm = (): void => {
  emit('update:modelValue', escoposDasOpcoes(checked.value, props.opcoes));
  emit('close');
};
</script>

<style scoped>
.acesso-modal-hint { color: #666; font-size: 0.875rem; margin-bottom: 12px; }
.acesso-modal-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px; }
.acesso-modal-count { font-size: 0.875rem; color: #555; }
</style>
